import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { asset } from '@/lib/api';
import { MagneticButton } from '@/components/MagneticButton';
import { usePrefersReducedMotion } from '@/lib/hooks';
import { EASE } from '@/lib/motion';

export const Hero = ({ hero }) => {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  if (!hero) return <div className='h-screen bg-[#20272A]' />;

  return (
    <section ref={ref} className='relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-[#20272A]' data-testid='home-hero'>
      <motion.div className='absolute inset-0' style={reduced ? {} : { y, scale }}>
        {reduced ? (
          <img src={asset(hero.poster)} alt='' className='h-full w-full object-cover' />
        ) : (
          <video
            className='h-full w-full object-cover'
            src={asset(hero.video)}
            poster={asset(hero.poster)}
            autoPlay
            muted
            loop
            playsInline
            preload='metadata'
          />
        )}
      </motion.div>

      <div
        className='absolute inset-0'
        style={{ background: 'linear-gradient(180deg, rgba(7,91,97,0.55) 0%, rgba(32,39,42,0.6) 45%, rgba(32,39,42,0.92) 100%)' }}
      />
      <div className='grain absolute inset-0' />

      <motion.div className='relative z-10 flex h-full items-center' style={reduced ? {} : { y: textY, opacity }}>
        <div className='container-x grid w-full items-center gap-10 lg:grid-cols-12'>
          <div className='lg:col-span-8'>
            <motion.div
              className='flex items-center gap-3'
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
            >
              <span className='h-px w-10 bg-[#B88A3B]' />
              <span className='eyebrow text-[#B88A3B]'>{hero.eyebrow}</span>
            </motion.div>

            <h1 className='mt-6 max-w-4xl font-head text-[2.6rem] leading-[1.02] tracking-[-0.025em] text-[#F6F5F1] sm:text-6xl lg:text-[4.4rem]'>
              {String(hero.headline).split(' ').map((word, i) => (
                <span key={i} className='inline-block overflow-hidden align-top pr-[0.28ch]'>
                  <motion.span
                    className='inline-block'
                    initial={{ y: '110%' }}
                    animate={{ y: '0%' }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.35 + i * 0.06 }}
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              className='mt-7 max-w-xl text-base text-[#F6F5F1]/75 sm:text-lg'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.7 }}
            >
              {hero.subheadline}
            </motion.p>

            <motion.div
              className='mt-9 flex flex-wrap gap-4'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
            >
              <MagneticButton to={hero.primaryCta?.href || '/services'} variant='gold' data-testid='hero-enquiry-button' data-cursor='EXPLORE'>
                {hero.primaryCta?.label || 'View Our Capabilities'}
              </MagneticButton>
              <MagneticButton to={hero.secondaryCta?.href || '/projects'} variant='secondary' className='text-[#F6F5F1]' data-testid='hero-projects-button' data-cursor='VIEW'>
                {hero.secondaryCta?.label || 'Explore Projects'}
              </MagneticButton>
            </motion.div>
          </div>

          {/* legacy overlays */}
          <motion.div
            className='lg:col-span-4'
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1 }}
          >
            <div className='flex flex-row gap-6 lg:flex-col lg:items-end lg:gap-7'>
              {(hero.overlays || []).map((o) => (
                <div key={o.label} className='border-l border-[#B88A3B]/50 pl-4 lg:text-right lg:border-l-0 lg:border-r lg:pl-0 lg:pr-4'>
                  <div className='font-mono-num text-2xl font-medium text-[#B88A3B] sm:text-3xl'>{o.value}</div>
                  <div className='mt-1 text-[11px] uppercase tracking-wider text-[#F6F5F1]/55'>{o.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className='absolute bottom-7 left-1/2 z-10 -translate-x-1/2'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
      >
        <motion.div
          className='flex flex-col items-center gap-2 text-[#F6F5F1]/55'
          animate={reduced ? {} : { y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <span className='eyebrow text-[10px]'>Scroll</span>
          <ChevronDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
};
